// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Determines whether we can traverse the object
 */
export function canTraverseObject(obj: any): boolean {
  if (obj !== Object(obj) || typeof obj === 'function') {
    return false;
  }
  const str = Object.prototype.toString.call(obj);
  if (str === '[object Date]' || str === '[object RegExp]' || str === '[object Boolean]') {
    return false;
  }
  return true;
}

/**
 * Processes an object to create a new one
 */
export function traverseObject(
  obj: any,
  processKey: (key: string | number, value: any, obj: any) => string = null,
  processValue: (value: any, key: string | number, obj: any) => any = (value) =>
    traverseObject(value, processKey)
): any {
  if (!canTraverseObject(obj)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((value, i) => processValue(value, i, obj));
  }

  const result: { [key: string]: any } = {};
  Object.entries(obj).forEach(([key, value]) => {
    const processedKey: string = processKey ? processKey(key, value, obj) : key;
    const processedValue = processValue ? processValue(value, key, obj) : value;
    result[processedKey] = processedValue;
  });

  return result;
}

/**
 * Creates a function that can recursively process the keys of an object
 */
export default function createKeyProcessor(
  processKeyAndPath: (
    key: string | number,
    path?: (string | number)[],
    value?: any,
    parent?: any
  ) => string,
  shouldProcessPathValue: (path: (string | number)[]) => boolean = () => true
) {
  return (response: any) => {
    const pathParts: (string | number)[] = [];
    const processKey = (key: string | number, value: any, parent: any) =>
      processKeyAndPath(key, pathParts, value, parent);
    const processValue = (value: any, key: string | number) => {
      pathParts.push(key);
      const result =
        shouldProcessPathValue && shouldProcessPathValue(pathParts)
          ? traverseObject(value, processKey, processValue)
          : value;
      pathParts.pop();
      return result;
    };
    return traverseObject(response, processKey, processValue);
  };
}

const REGEX_WORDS = new RegExp(
  `(${[
    // shortened terms e.g. 'A11Y' or 'A11y'
    '[A-Z]\\d+[A-Z](?=[A-Z\\s_-]|$)', // 'A11Y'
    '[A-Z]\\d+[a-z](?=[A-Z\\d\\s_-]|$)', // 'A11y'
    '[a-z]\\d+[a-z](?=[A-Z\\d\\s_-]|$)', // 'a11y'
    // letter-prefixed numbers e.g. 'V10' or 'v10'
    '[A-Za-z]\\d+(?=[a-z]{2}|[A-Z\\s_-]|$)',
    // all-caps terms
    '[A-Z]+(?=[A-Z][a-z\\d]|[\\s_-]|$)',
    // terms that start with an uppercase letter
    '[A-Z][^A-Z\\d\\s_-]*',
    // standalone numbers
    '\\d+',
    // all other terms
    '[^A-Z\\d\\s_-]+',
  ].join('|')})`,
  'g'
);

export function words(value: string | number): string[] {
  if (typeof value === 'number') {
    return [value.toString()];
  }
  return value.match(REGEX_WORDS) || [];
}

export function camelize(value: string | number): string {
  return words(value)
    .map((word, i) =>
      i ? `${word[0].toUpperCase()}${word.substring(1).toLowerCase()}` : word.toLowerCase()
    )
    .join('');
}

export function decamelize(value: string | number, delimiter = '_'): string {
  return words(value)
    .map((word) => word.toLowerCase())
    .join(delimiter);
}

export const camelizeKeys = createKeyProcessor(camelize);
export const decamelizeKeys = createKeyProcessor((value: string | number) =>
  decamelize(value, '_')
);
